import { useEffect, useRef } from 'react';

import { type OverlayEngine, type OverlayLayerHandle } from './OverlayEngine';
import { type OverlayLayerOption } from './type';

/**
 * 주어진 엔진에 오버레이 레이어를 등록하는 중립 훅. 앱 모듈(viewer·simulation)의 Provider 와
 * 컨텍스트에 의존하지 않으므로 각 모듈은 자기 엔진을 넘기는 얇은 래퍼만 만들면 됩니다.
 *
 * - 마운트(또는 `engine`·`option.id` 변경)에서 등록하고 언마운트에서 해제합니다.
 * - 렌더마다 옵션을 갱신하되, 같은 옵션 객체면(소비자가 `useMemo` 로 고정한 경우) 갱신을 건너뛰어
 *   불필요한 다시 그리기 요청을 만들지 않습니다.
 */
export const useOverlayEngineLayer = <THit = unknown>(
  engine: OverlayEngine,
  option: OverlayLayerOption<THit>,
): void => {
  const handleRef = useRef<Nullish<OverlayLayerHandle>>(null);
  const optionRef = useRef(option as OverlayLayerOption);
  optionRef.current = option as OverlayLayerOption;
  // 엔진에 마지막으로 넘긴 옵션 객체. 등록 직후의 갱신 효과가 같은 객체를 다시 넘기지 않게 합니다.
  const appliedRef = useRef<Nullish<OverlayLayerOption>>(null);

  useEffect(() => {
    const handle = engine.registerLayer(optionRef.current);
    handleRef.current = handle;
    appliedRef.current = optionRef.current;
    return () => {
      handle.unregister();
      if (handleRef.current === handle) handleRef.current = null;
      appliedRef.current = null;
    };
  }, [engine, option.id]);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle || appliedRef.current === optionRef.current) return;
    appliedRef.current = optionRef.current;
    handle.update(optionRef.current);
  });
};
